import {OrdersListDTO} from "@dto/OrdersListDTO";

export type OrdersListMessage = {
    type: 'orders-list'
    pollerKey: string
    ordersList: OrdersListDTO
}

export type LeaderReleasedMessage = {
    type: 'leader-released'
    pollerKey: string
}

export type OrdersPollerMessage = OrdersListMessage | LeaderReleasedMessage

export interface OrdersPollerChannel {
    publish(message: OrdersPollerMessage): void

    subscribe(listener: (message: OrdersPollerMessage) => void): () => void
}

const CHANNEL_NAME = 'orders-poller'

// The fallback writes every message under this key; other tabs get it as a `storage` event.
export const STORAGE_MESSAGE_KEY = 'orders-poller-message'

/**
 * BroadcastChannel delivers a message to the other tabs of the browser, never back to the same channel object,
 * so a tab does not receive its own messages.
 */
export class BroadcastOrdersPollerChannel implements OrdersPollerChannel {
    private readonly channel: BroadcastChannel

    constructor() {
        this.channel = new BroadcastChannel(CHANNEL_NAME)
    }

    publish(message: OrdersPollerMessage): void {
        this.channel.postMessage(message)
    }

    subscribe(listener: (message: OrdersPollerMessage) => void): () => void {
        const onMessage = (event: MessageEvent) => {
            if (!isOrdersPollerMessage(event.data)) return
            listener(event.data)
        }
        this.channel.addEventListener('message', onMessage)

        return () => this.channel.removeEventListener('message', onMessage)
    }
}

/**
 * For browsers without BroadcastChannel (old Safari on venue tablets): the `storage` event fires in every other tab
 * of the same origin when a key changes. The message is removed right away, so it never stays in localStorage.
 */
export class StorageOrdersPollerChannel implements OrdersPollerChannel {
    constructor(private readonly storage: Storage) {
    }

    publish(message: OrdersPollerMessage): void {
        // A nonce makes two equal messages in a row still a change, so the second one fires an event too.
        const storedMessage = JSON.stringify({message, nonce: Math.random()})
        try {
            this.storage.setItem(STORAGE_MESSAGE_KEY, storedMessage)
            this.storage.removeItem(STORAGE_MESSAGE_KEY)
        } catch (e) {
            // Storage is full or blocked: the other tabs poll on their own once their leader lease expires.
        }
    }

    subscribe(listener: (message: OrdersPollerMessage) => void): () => void {
        const onStorage = (event: StorageEvent) => {
            if (event.key !== STORAGE_MESSAGE_KEY) return
            if (!event.newValue) return

            const message = parseStoredMessage(event.newValue)
            if (message === null) return
            listener(message)
        }
        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }
}

export const createOrdersPollerChannel = (): OrdersPollerChannel | null => {
    if (typeof BroadcastChannel !== 'undefined') {
        return new BroadcastOrdersPollerChannel()
    }

    const storage = findLocalStorage()
    if (storage !== null) {
        return new StorageOrdersPollerChannel(storage)
    }

    return null
}

export const findLocalStorage = (): Storage | null => {
    try {
        if (typeof window === 'undefined') return null
        if (!window.localStorage) return null

        return window.localStorage
    } catch (e) {
        // Blocked storage throws on access.
        return null
    }
}

const parseStoredMessage = (storedMessage: string): OrdersPollerMessage | null => {
    try {
        const parsed = JSON.parse(storedMessage)
        if (!isOrdersPollerMessage(parsed?.message)) return null

        return parsed.message
    } catch (e) {
        return null
    }
}

const isOrdersPollerMessage = (data: unknown): data is OrdersPollerMessage => {
    if (typeof data !== 'object' || data === null) return false

    const message = data as Partial<OrdersPollerMessage>
    if (typeof message.pollerKey !== 'string') return false
    if (message.type === 'orders-list') return true
    if (message.type === 'leader-released') return true

    return false
}
